/**
 * Free-shipping sign-up landing page: signed-up state.
 *
 * The hero section wraps itself in <signup-landing-component>. When the
 * visitor signs up, every element on the page marked
 * data-signup-state="unsigned" is hidden and every one marked
 * data-signup-state="signed" is shown (the code card in the hero, the
 * "Enter CODE at checkout" line in the flavour picker). The state is
 * remembered in localStorage so it survives a reload.
 *
 * Sign-up is detected from any of:
 *  - Klaviyo's embedded form: the `klaviyoForms` window event with type
 *    "submit" or "stepSubmit" (optionally matched to data-klaviyo-form-id).
 *  - The theme's newsletter-form block in direct-to-Klaviyo mode: it
 *    dispatches `newsletter-form:success`.
 *  - The theme's newsletter-form block in Shopify mode: the page reloads
 *    with ?contact_posted=true.
 *
 * In the theme editor, data-preview-signed forces the signed-up view so the
 * merchant can style the code card.
 */

const STORAGE_KEY = 'jamu_freeship_signed';
const COPIED_RESET_MS = 1800;
/* How long to give Klaviyo to render its embedded form before showing the fallback. */
const EMBED_FALLBACK_MS = 5000;

function readSigned() {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSigned() {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // Private mode or blocked storage: the state just won't persist.
  }
}

/** @param {boolean} signed */
function applyState(signed) {
  for (const el of document.querySelectorAll('[data-signup-state]')) {
    const showWhenSigned = el.getAttribute('data-signup-state') === 'signed';
    el.toggleAttribute('hidden', showWhenSigned !== signed);
  }
}

class SignupLandingComponent extends HTMLElement {
  #embedOpened = false;
  #embedTimer = 0;

  connectedCallback() {
    const preview = this.hasAttribute('data-preview-signed');
    const postedViaShopify = new URL(window.location.href).searchParams.get('contact_posted') === 'true';
    if (postedViaShopify) writeSigned();

    applyState(preview || readSigned());

    window.addEventListener('klaviyoForms', this.#onKlaviyoForm);
    this.addEventListener('newsletter-form:success', this.#onNewsletterSuccess);
    this.addEventListener('click', this.#onClick);
    this.#armEmbedFallback();
  }

  disconnectedCallback() {
    window.clearTimeout(this.#embedTimer);
    window.removeEventListener('klaviyoForms', this.#onKlaviyoForm);
    this.removeEventListener('newsletter-form:success', this.#onNewsletterSuccess);
    this.removeEventListener('click', this.#onClick);
  }

  markSigned() {
    writeSigned();
    applyState(true);
    const card = this.querySelector('[data-signup-state="signed"]');
    if (card instanceof HTMLElement) card.focus({ preventScroll: false });
  }

  /**
   * Klaviyo hides embedded forms from existing profiles and honours any
   * delay/scroll rules; tracker blockers stop it loading at all. If the embed
   * has not opened after EMBED_FALLBACK_MS, show the fallback (a short
   * message plus the theme's own form) so the space is never blank.
   */
  #armEmbedFallback() {
    const fallback = this.querySelector('[data-klaviyo-fallback]');
    if (!fallback || !this.getAttribute('data-klaviyo-form-id')) return;

    this.#embedTimer = window.setTimeout(() => {
      if (!this.#embedOpened) fallback.removeAttribute('hidden');
    }, EMBED_FALLBACK_MS);
  }

  #onKlaviyoForm = (event) => {
    const detail = event instanceof CustomEvent ? event.detail : null;
    if (!detail) return;

    const wanted = this.getAttribute('data-klaviyo-form-id');
    if (wanted && detail.formId && String(detail.formId) !== wanted) return;

    if (detail.type === 'embedOpen') {
      this.#embedOpened = true;
      window.clearTimeout(this.#embedTimer);
      this.querySelector('[data-klaviyo-fallback]')?.setAttribute('hidden', '');
      return;
    }

    if (detail.type === 'submit' || detail.type === 'stepSubmit') this.markSigned();
  };

  #onNewsletterSuccess = () => {
    this.markSigned();
  };

  #onClick = async (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-copy-code]') : null;
    if (!(target instanceof HTMLButtonElement)) return;

    const code = target.getAttribute('data-copy-code') ?? '';
    const copyLabel = target.getAttribute('data-copy-label') ?? target.textContent;
    const copiedLabel = target.getAttribute('data-copied-label') ?? copyLabel;

    try {
      await navigator.clipboard.writeText(code);
      target.textContent = copiedLabel;
    } catch {
      // Clipboard blocked: leave the label alone; the code is visible beside the button.
      return;
    }

    window.setTimeout(() => {
      target.textContent = copyLabel;
    }, COPIED_RESET_MS);
  };
}

if (!customElements.get('signup-landing-component')) {
  customElements.define('signup-landing-component', SignupLandingComponent);
}